import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Zap, ShieldCheck, LineChart, TrendingDown, Leaf, Users, Building2, BadgeCheck, Phone, Clock, Gift, Lock, FileText, Scale, Check, Target } from "lucide-react";
import { TRV_BASE_PRICE_EUR_KWH, TRV_BASE_SUBSCRIPTION_EUR_MONTH } from "@/config/regulatedReference";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import AudienceToggle from "@/components/AudienceToggle";
import { useSiteContent } from "@/lib/useSiteContent";
import { useTariffs } from "@/lib/useTariffs";
import { keysForMany } from "@/config/cmsSections";
import CmsImage from "@/components/CmsImage";
import CmsBackground from "@/components/CmsBackground";
import AudienceCardsSection from "@/components/AudienceCardsSection";

// Keys CMS de toutes les sections home — tirées de la config centrale
const CMS_KEYS = keysForMany(
    "common",
    "home-hero-b2c",
    "home-hero-b2b",
    "home-hero-common",
    "home-stats",
    "home-how",
    "home-simulator",
    "home-audience-b2c",
    "home-audience-b2b",
    "home-analysis",
    "home-cta-strip",
    "home-engagements",
);

export default function Home() {
    const [audience, setAudience] = useState("particulier");
    const [kwh, setKwh] = useState([4500]);
    const [pricePerKwh, setPricePerKwh] = useState([TRV_BASE_PRICE_EUR_KWH]);
    const [greenOnly, setGreenOnly] = useState(false);

    const current = useMemo(() => kwh[0] * pricePerKwh[0] + TRV_BASE_SUBSCRIPTION_EUR_MONTH * 12, [kwh, pricePerKwh]);
    const optimized = useMemo(() => kwh[0] * (greenOnly ? 0.2123 : 0.2089) + 12.20 * 12, [kwh, greenOnly]);
    const savings = Math.max(0, current - optimized);

    const isB2C = audience === "particulier";
    const c = useSiteContent(CMS_KEYS);

    const HOW_ICONS = [FileText, Scale, ShieldCheck];

    // ─── Tarifs CRE réels (TRV Bleu / PPR Engie) — initialise le slider ───
    const tariffs = useTariffs();
    const [userTouchedPrice, setUserTouchedPrice] = useState(false);
    useEffect(() => {
        if (userTouchedPrice) return; // ne pas écraser si l'utilisateur a déjà touché le slider
        if (!tariffs) return;
        // B2C particulier → TRV Bleu base ; B2B pro → moyenne marché HT
        const realPrice = isB2C
            ? tariffs?.electricity?.trv_base_kwh
            : tariffs?.electricity?.pro_market_avg_kwh ?? tariffs?.electricity?.trv_base_kwh;
        if (realPrice && realPrice >= 0.15 && realPrice <= 0.32) {
            setPricePerKwh([realPrice]);
        }
    }, [tariffs, isB2C, userTouchedPrice]);

    // ─── Animation au scroll : section stats apparaît avec stagger ───
    const statsRef = useRef(null);
    const [statsVisible, setStatsVisible] = useState(false);
    useEffect(() => {
        if (!statsRef.current) return;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setStatsVisible(true);
                    obs.disconnect();
                }
            },
            { threshold: 0.3 }
        );
        obs.observe(statsRef.current);
        return () => obs.disconnect();
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO — plein écran sous le header flottant (image de fond jusqu'au top viewport) */}
            <section className="relative em-grain overflow-hidden -mt-20 md:-mt-20 min-h-screen flex flex-col">
                <CmsBackground cmsKey={isB2C ? "home.hero.b2c.bg" : "home.hero.b2b.bg"} />
                <div className="relative z-10 em-container pt-24 md:pt-28 pb-12 w-full flex-grow">
                    <div className="flex justify-center mb-6">
                        <AudienceToggle value={audience} onChange={setAudience} />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-7">
                            <p className="em-eyebrow mb-4" data-testid="hero-eyebrow">
                                {isB2C
                                    ? c("home.hero.b2c.eyebrow", "Particuliers · Électricité · Gaz")
                                    : c("home.hero.b2b.eyebrow", "Entreprises · Multi-sites · Énergie verte")}
                            </p>
                            <h1
                                className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-primary leading-[1.05]"
                                dangerouslySetInnerHTML={{
                                    __html: isB2C
                                        ? c("home.hero.b2c.title", "L'énergie <span class=\"bg-accent text-primary px-3 rounded-md\">moins chère</span><br /> qui vous ressemble.")
                                        : c("home.hero.b2b.title", "L'achat d'énergie<br /> <span class=\"bg-accent text-primary px-3 rounded-md\">stratégique</span> pour les pros."),
                                }}
                            />
                            <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-xl leading-relaxed">
                                {isB2C
                                    ? c("home.hero.b2c.subtitle", "Comparez en 2 minutes l'ensemble des fournisseurs français. Économisez jusqu'à 35% sur vos factures, avec une transparence totale sur nos commissions.")
                                    : c("home.hero.b2b.subtitle", "Pilotez vos contrats électricité et gaz multi-sites. Veille marché continue, négociation experte et reporting consommation.")}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 mt-8">
                                <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 px-7 h-12" data-testid="hero-cta-primary">
                                    <Link to="/comparateur">
                                        {c("home.hero.cta_primary", "Lancer le comparateur")}
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Link>
                                </Button>
                                <Button asChild size="lg" variant="outline" className="rounded-full px-7 h-12 border-primary/20" data-testid="hero-cta-secondary">
                                    <Link to="/devis">
                                        <Phone className="h-4 w-4 mr-2" />
                                        {c("home.hero.cta_secondary", "Demander un devis")}
                                    </Link>
                                </Button>
                            </div>
                            <div className="flex items-center gap-3 mt-8 text-sm text-muted-foreground">
                                <BadgeCheck className="h-4 w-4 text-primary" />
                                {c("home.hero.trust", "Sans engagement · Service gratuit pour le client · Conformité ORIAS")}
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="relative rounded-3xl overflow-hidden shadow-xl ring-1 ring-border">
                                <CmsImage
                                    cmsKey={isB2C ? "home.hero.b2c.image" : "home.hero.b2b.image"}
                                    fallback={isB2C ? "/img/hero-b2c.png" : "/img/hero-b2b.png"}
                                    alt={isB2C ? "Maison résidentielle avec panneaux solaires" : "Bâtiment d'entreprise éco-responsable"}
                                    className="w-full h-[420px] md:h-[520px] transition-all duration-500"
                                    data-testid="hero-image"
                                />
                                <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur rounded-2xl p-4 shadow-lg border border-border">
                                    <p className="text-xs tracking-[0.18em] uppercase text-muted-foreground">{c("home.hero.savings_label", "Économies estimées")}</p>
                                    <p className="font-display text-3xl font-black text-primary mt-1">
                                        {isB2C
                                            ? c("home.hero.b2c.savings", "468 €/an")
                                            : c("home.hero.b2b.savings", "12 400 €/an")}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        sur un profil {isB2C
                                            ? c("home.hero.b2c.savings_profile", "résidentiel moyen 4500 kWh")
                                            : c("home.hero.b2b.savings_profile", "entreprise mono-site 100 MWh")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* STATS — directement sur le fond du hero, pas de strip de fond */}
                <div className="relative z-10" ref={statsRef}>
                    <div className="em-container pb-8 md:pb-10 pt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8" data-testid="stats-row">
                        {(() => {
                            const defaultStats = {
                                1: { value: "−30 %",  label: "Économies moyennes" },
                                2: { value: "1 €",    label: "Isolation combles" },
                                3: { value: "100 %",  label: "Gratuit & transparent" },
                                4: { value: "24 h",   label: "Délai de réponse" },
                                5: { value: "2 min",  label: "Comparaison express" },
                            };
                            return [1, 2, 3, 4, 5].map((idx) => {
                                const value = c(`home.stats.${idx}.value`, defaultStats[idx].value);
                                const label = c(`home.stats.${idx}.label`, defaultStats[idx].label);
                                // Si l'admin a vidé une stat dans le CMS, on la masque
                                if (!value || !label) return null;
                                // Stagger : 220ms entre chaque, durée 1100ms — apparition fluide et lisible
                                const delay = (idx - 1) * 220;
                                return (
                                    <div
                                        key={idx}
                                        className={`transform transition-all ease-out ${
                                            statsVisible
                                                ? "opacity-100 translate-y-0 blur-0"
                                                : "opacity-0 translate-y-10 blur-[3px]"
                                        }`}
                                        style={{
                                            transitionDuration: "1100ms",
                                            transitionDelay: `${delay}ms`,
                                        }}
                                    >
                                        <p
                                            className="font-display text-3xl md:text-4xl font-black text-[#0A3B2A]"
                                            style={{ textShadow: "0 1px 2px rgba(255,255,255,0.6), 0 0 18px rgba(255,255,255,0.4)" }}
                                        >
                                            {value}
                                        </p>
                                        <p
                                            className={`text-xs md:text-sm font-semibold text-[#0A3B2A]/80 mt-1.5 transition-opacity ease-out ${
                                                statsVisible ? "opacity-100" : "opacity-0"
                                            }`}
                                            style={{
                                                transitionDuration: "800ms",
                                                transitionDelay: `${delay + 300}ms`,
                                                textShadow: "0 1px 2px rgba(255,255,255,0.6)",
                                            }}
                                        >
                                            {label}
                                        </p>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>
            </section>

            {/* AUDIENCE CARDS — bloc premium "Deux profils, une même promesse" */}
            <AudienceCardsSection />

            {/* HOW IT WORKS — Premium B2B */}
            <section
                className="relative overflow-hidden py-24 md:py-32 bg-[#0A3B2A] text-white"
                data-testid="how-it-works"
            >
                {/* Décor : halos lumineux subtils en arrière-plan */}
                <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#B8E04A] rounded-full blur-[120px] opacity-[0.08]" />
                    <div className="absolute -bottom-40 -left-32 w-[600px] h-[600px] bg-[#2E7D62] rounded-full blur-[120px] opacity-[0.15]" />
                </div>
                {/* Allow CMS background override */}
                <CmsBackground cmsKey="home.how.bg" />

                <div className="relative z-10 em-container">
                    {/* En-tête */}
                    <div className="max-w-3xl">
                        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#B8E04A] mb-4">
                            {c("home.how.eyebrow", "Comment ça marche")}
                        </p>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                            {c("home.how.title", "Trois étapes, zéro friction.")}
                        </h2>
                        <p className="text-lg md:text-xl text-white/70 mt-6 leading-relaxed">
                            {c("home.how.subtitle", "Un service plus simple que de payer sa facture.")}
                        </p>
                    </div>

                    {/* Cartes étapes avec ligne de progression desktop */}
                    <div className="relative mt-16 md:mt-20">
                        {/* Ligne de progression horizontale (desktop uniquement) */}
                        <div
                            aria-hidden="true"
                            className="hidden md:block absolute top-[88px] left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-[#B8E04A]/30 to-transparent z-0"
                        />

                        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                            {[1, 2, 3].map((idx) => {
                                const Icon = HOW_ICONS[idx - 1];
                                const defaultTitles = {
                                    1: "Vous nous transmettez votre facture",
                                    2: "Nous comparons les meilleures offres",
                                    3: "Vous choisissez, nous nous occupons du reste",
                                };
                                const defaultBodies = {
                                    1: "Ajoutez simplement votre dernière facture d'électricité ou de gaz. Nous analysons votre profil de consommation, vos tarifs actuels et vos conditions contractuelles.",
                                    2: "Energy Market consulte les fournisseurs, compare les prix, les conditions, la durée d'engagement et les opportunités d'économie adaptées à votre activité.",
                                    3: "Vous validez l'offre la plus avantageuse. Nous gérons les démarches administratives, le changement de fournisseur et le suivi de votre contrat, sans coupure ni complication.",
                                };
                                // Liens : étape 1 → upload facture (form devis), 2 → comparateur, 3 → devis
                                const stepLinks = {
                                    1: "/devis",
                                    2: "/comparateur",
                                    3: "/devis",
                                };
                                const stepCtaLabels = {
                                    1: "Envoyer ma facture",
                                    2: "Lancer la comparaison",
                                    3: "Démarrer ma demande",
                                };
                                const linkTo = stepLinks[idx];
                                const ctaLabel = stepCtaLabels[idx];

                                const cardInner = (
                                    <>
                                        {/* Numéro + icône */}
                                        <div className="flex items-start justify-between mb-8">
                                            <div className="h-14 w-14 rounded-2xl bg-[#B8E04A]/10 ring-1 ring-[#B8E04A]/20 flex items-center justify-center transition-all duration-500 group-hover:bg-[#B8E04A]/15 group-hover:ring-[#B8E04A]/40">
                                                <Icon className="h-6 w-6 text-[#B8E04A]" strokeWidth={1.75} />
                                            </div>
                                            <span className="font-display text-6xl lg:text-7xl font-black tracking-tighter text-[#B8E04A]/25 leading-none select-none">
                                                0{idx}
                                            </span>
                                        </div>

                                        {/* Titre */}
                                        <h3 className="font-display text-xl lg:text-2xl font-bold text-white leading-tight">
                                            {c(`home.how.step${idx}.title`, defaultTitles[idx])}
                                        </h3>

                                        {/* Description */}
                                        <p className="text-white/70 mt-4 leading-relaxed text-[15px]">
                                            {c(`home.how.step${idx}.body`, defaultBodies[idx])}
                                        </p>

                                        {/* CTA chip qui apparaît au hover */}
                                        {linkTo && (
                                            <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#B8E04A] opacity-70 group-hover:opacity-100 transition-opacity">
                                                <span>{ctaLabel}</span>
                                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                            </div>
                                        )}
                                    </>
                                );

                                const cardClass = "group relative block rounded-3xl bg-white/[0.04] backdrop-blur-md ring-1 ring-white/10 p-8 lg:p-9 transition-all duration-500 hover:bg-white/[0.07] hover:ring-[#B8E04A]/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#B8E04A]/5";

                                return linkTo ? (
                                    <Link key={idx} to={linkTo} className={cardClass} aria-label={defaultTitles[idx]}>
                                        {cardInner}
                                    </Link>
                                ) : (
                                    <div key={idx} className={cardClass}>
                                        {cardInner}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Bloc de réassurance */}
                    <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 text-center md:text-left">
                        <div className="inline-flex items-center gap-2 text-sm text-white/60">
                            <Check className="h-4 w-4 text-[#B8E04A]" strokeWidth={2.5} />
                            <span>{c("home.how.reassure1", "Aucune coupure")}</span>
                        </div>
                        <span className="hidden md:block text-white/20">·</span>
                        <div className="inline-flex items-center gap-2 text-sm text-white/60">
                            <Check className="h-4 w-4 text-[#B8E04A]" strokeWidth={2.5} />
                            <span>{c("home.how.reassure2", "Aucun engagement inutile")}</span>
                        </div>
                        <span className="hidden md:block text-white/20">·</span>
                        <div className="inline-flex items-center gap-2 text-sm text-white/60">
                            <Check className="h-4 w-4 text-[#B8E04A]" strokeWidth={2.5} />
                            <span>{c("home.how.reassure3", "Accompagnement de bout en bout")}</span>
                        </div>
                    </div>

                    <p className="text-center text-white/40 text-sm mt-6 italic">
                        {c("home.how.tagline", "Vous gardez le contrôle. Nous gérons la complexité.")}
                    </p>
                </div>
            </section>

            {/* SAVINGS SIMULATOR */}
            <section className="relative bg-secondary overflow-hidden" data-testid="savings-simulator">
                <CmsBackground cmsKey="home.simulator.bg" />
                <div className="relative z-10 em-container py-20 md:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5">
                            <p className="em-eyebrow mb-3">{c("home.simulator.eyebrow", "Simulateur instantané")}</p>
                            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary">
                                {c("home.simulator.title", "Combien pouvez-vous économiser cette année ?")}
                            </h2>
                            <p className="text-muted-foreground mt-5 leading-relaxed">
                                {c("home.simulator.body", "Glissez les curseurs pour estimer vos économies à partir de votre consommation annuelle et de votre tarif actuel. Calcul transparent, en temps réel.")}
                            </p>
                            <Button asChild size="lg" className="rounded-full mt-7 bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="simulator-cta">
                                <Link to="/comparateur">
                                    {c("home.simulator.cta", "Voir le détail des offres")}
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Link>
                            </Button>
                        </div>

                        <div className="lg:col-span-7">
                            <Card className="border-border rounded-3xl shadow-sm bg-card">
                                <CardContent className="p-8 space-y-7">
                                    <div>
                                        <div className="flex justify-between mb-3">
                                            <Label className="font-semibold">{c("home.simulator.label_kwh", "Consommation annuelle (kWh)")}</Label>
                                            <span className="font-mono text-primary font-bold" data-testid="simulator-kwh-value">{kwh[0]} kWh</span>
                                        </div>
                                        <Slider value={kwh} onValueChange={setKwh} min={1000} max={30000} step={100} data-testid="simulator-kwh-slider" />
                                    </div>
                                    <div>
                                        <div className="flex justify-between mb-3">
                                            <Label className="font-semibold">
                                                {c("home.simulator.label_price", "Prix du kWh actuel (€)")}
                                                {tariffs && !userTouchedPrice && (
                                                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                                                        · TRV CRE en vigueur
                                                    </span>
                                                )}
                                            </Label>
                                            <span className="font-mono text-primary font-bold" data-testid="simulator-price-value">{pricePerKwh[0].toFixed(4)} €</span>
                                        </div>
                                        <Slider
                                            value={pricePerKwh}
                                            onValueChange={(v) => { setPricePerKwh(v); setUserTouchedPrice(true); }}
                                            min={0.15}
                                            max={0.32}
                                            step={0.0005}
                                            data-testid="simulator-price-slider"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between pt-2">
                                        <div className="flex items-center gap-2">
                                            <Leaf className="h-4 w-4 text-primary" />
                                            <Label htmlFor="green-only" className="font-semibold">{c("home.simulator.label_green", "Énergie verte uniquement")}</Label>
                                        </div>
                                        <Switch id="green-only" checked={greenOnly} onCheckedChange={setGreenOnly} data-testid="simulator-green-switch" />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-6 border-t border-border">
                                        <div>
                                            <p className="text-xs uppercase tracking-wider text-muted-foreground">{c("home.simulator.label_current", "Coût actuel")}</p>
                                            <p className="font-display text-2xl font-black mt-1">{current.toFixed(0)} €/an</p>
                                        </div>
                                        <div className="bg-accent rounded-2xl p-4 -my-2">
                                            <p className="text-xs uppercase tracking-wider text-accent-foreground/70">{c("home.simulator.label_savings", "Économie")}</p>
                                            <p className="font-display text-3xl font-black text-accent-foreground mt-1 flex items-baseline gap-1" data-testid="simulator-savings">
                                                <TrendingDown className="h-5 w-5" />
                                                {savings.toFixed(0)} €
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* AUDIENCE SECTIONS */}
            <section className="em-section em-container">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="audience-sections">
                    <Card className="rounded-3xl em-card-hover border-border bg-card overflow-hidden">
                        <div className="h-44 bg-secondary flex items-center justify-center">
                            <Users className="h-16 w-16 text-primary" strokeWidth={1.5} />
                        </div>
                        <CardContent className="p-7">
                            <p className="em-eyebrow mb-3">{c("home.audience.b2c.eyebrow", "Particuliers")}</p>
                            <h3 className="font-display text-3xl font-bold text-primary">{c("home.audience.b2c.title", "Votre foyer mérite la meilleure offre")}</h3>
                            <ul className="mt-5 space-y-3 text-muted-foreground">
                                <li className="flex gap-3"><Zap className="h-5 w-5 text-primary shrink-0 mt-0.5" /> {c("home.audience.b2c.bullet1", "Électricité et gaz comparés en temps réel")}</li>
                                <li className="flex gap-3"><Leaf className="h-5 w-5 text-primary shrink-0 mt-0.5" /> {c("home.audience.b2c.bullet2", "Filtres énergie verte et 100% renouvelable")}</li>
                                <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" /> {c("home.audience.b2c.bullet3", "Souscription assistée par chat ou téléphone")}</li>
                            </ul>
                            <Button asChild className="rounded-full mt-6 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="b2c-cta">
                                <Link to="/comparateur?audience=b2c">{c("home.audience.b2c.cta", "Comparer les offres particuliers")}</Link>
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="rounded-3xl em-card-hover border-border bg-primary text-primary-foreground overflow-hidden">
                        <div className="h-44 bg-primary/85 flex items-center justify-center border-b border-primary-foreground/10">
                            <Building2 className="h-16 w-16 text-accent" strokeWidth={1.5} />
                        </div>
                        <CardContent className="p-7">
                            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">{c("home.audience.b2b.eyebrow", "Entreprises")}</p>
                            <h3 className="font-display text-3xl font-bold mt-3">{c("home.audience.b2b.title", "Optimisation B2B sans concession")}</h3>
                            <ul className="mt-5 space-y-3 text-primary-foreground/80">
                                <li className="flex gap-3"><Sparkles className="h-5 w-5 text-accent shrink-0 mt-0.5" /> {c("home.audience.b2b.bullet1", "Stratégie d'achat sur mesure pluri-annuelle")}</li>
                                <li className="flex gap-3"><LineChart className="h-5 w-5 text-accent shrink-0 mt-0.5" /> {c("home.audience.b2b.bullet2", "Reporting consommation multi-sites")}</li>
                                <li className="flex gap-3"><BadgeCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" /> {c("home.audience.b2b.bullet3", "CEE, autoconsommation, bornes de recharge")}</li>
                            </ul>
                            <Button asChild className="rounded-full mt-6 bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="b2b-cta">
                                <Link to="/comparateur?audience=b2b">{c("home.audience.b2b.cta", "Optimiser mes contrats pros")}</Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* ENGAGEMENTS — Pourquoi choisir Energy Market ? */}
            <section className="em-section em-container" data-testid="home-engagements">
                <div className="max-w-2xl">
                    <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#06422B]/70 mb-3">
                        {c("home.engagements.eyebrow", "Nos engagements")}
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#06422B] leading-[1.1]">
                        {c("home.engagements.title", "Pourquoi choisir Energy Market ?")}
                    </h2>
                    <p className="text-base md:text-lg text-[#5F6F68] mt-4 leading-relaxed">
                        {c("home.engagements.subtitle", "Un courtier indépendant qui simplifie chaque étape : du diagnostic à la signature, et bien après.")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mt-12">
                    {[
                        {
                            icon: BadgeCheck,
                            idx: 1,
                            defaultTitle: "Des tarifs négociés pour vous",
                            defaultBody: "Nous comparons les offres disponibles et identifions les contrats les plus compétitifs selon votre profil de consommation.",
                        },
                        {
                            icon: Clock,
                            idx: 2,
                            defaultTitle: "Un gain de temps immédiat",
                            defaultBody: "Plus besoin de contacter plusieurs fournisseurs ni de décoder seul les grilles tarifaires. Nous faisons le tri pour vous.",
                        },
                        {
                            icon: Target,
                            idx: 3,
                            defaultTitle: "Une offre adaptée à votre usage",
                            defaultBody: "Votre consommation, votre logement ou votre activité professionnelle servent de base à notre recommandation.",
                        },
                        {
                            icon: FileText,
                            idx: 4,
                            defaultTitle: "Des contrats plus clairs",
                            defaultBody: "Nous vous aidons à comprendre les points essentiels : prix, durée, engagement, évolution tarifaire et frais annexes.",
                        },
                        {
                            icon: ShieldCheck,
                            idx: 5,
                            defaultTitle: "Un suivi après la souscription",
                            defaultBody: "Nous restons attentifs aux évolutions du marché pour vous aider à changer d'offre lorsque cela devient pertinent.",
                        },
                    ].map(({ icon: Icon, idx, defaultTitle, defaultBody }) => (
                        <article
                            key={idx}
                            className="group flex flex-col rounded-2xl bg-[#FAFBF7] p-7 lg:p-8 border border-[rgba(6,66,43,0.12)] shadow-[0_4px_18px_-8px_rgba(6,66,43,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_28px_-8px_rgba(6,66,43,0.18)] hover:border-[rgba(6,66,43,0.25)]"
                        >
                            {/* Icône carrée arrondie verte pâle */}
                            <div className="h-12 w-12 rounded-xl bg-[#D7F2A3] flex items-center justify-center mb-5">
                                <Icon className="h-6 w-6 text-[#06422B]" strokeWidth={1.9} />
                            </div>

                            {/* Titre */}
                            <h3 className="font-display text-lg lg:text-xl font-bold text-[#06422B] leading-snug">
                                {c(`home.engagements.${idx}.title`, defaultTitle)}
                            </h3>

                            {/* Paragraphe */}
                            <p className="text-sm text-[#5F6F68] mt-3 leading-relaxed">
                                {c(`home.engagements.${idx}.body`, defaultBody)}
                            </p>
                        </article>
                    ))}
                </div>
            </section>

            {/* AI INSIGHTS */}
            <section className="relative em-section overflow-hidden">
                <CmsBackground cmsKey="home.analysis.bg" />
                <div className="relative z-10 em-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        <p className="em-eyebrow mb-3">{c("home.analysis.eyebrow", "Analyse de marché")}</p>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-primary">
                            {c("home.analysis.title", "Energy Market négocie pour vous.")}
                        </h2>
                        <p className="text-muted-foreground mt-5 leading-relaxed">
                            {c("home.analysis.body", "Notre moteur d'analyse décortique les marchés de l'énergie en temps réel, anticipe les variations tarifaires et personnalise chaque recommandation selon votre profil de consommation.")}
                        </p>
                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <div className="bg-secondary rounded-2xl p-5">
                                <p className="em-eyebrow">{c("home.analysis.kpi1.label", "Veille marché")}</p>
                                <p className="font-display text-lg font-bold mt-1">{c("home.analysis.kpi1.value", "24/7 temps réel")}</p>
                            </div>
                            <div className="bg-secondary rounded-2xl p-5">
                                <p className="em-eyebrow">{c("home.analysis.kpi2.label", "Recommandations")}</p>
                                <p className="font-display text-lg font-bold mt-1">{c("home.analysis.kpi2.value", "Personnalisées")}</p>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-6 order-1 lg:order-2">
                        <div className="rounded-3xl overflow-hidden ring-1 ring-border shadow-xl">
                            <CmsImage cmsKey="home.analysis.image" fallback="/img/market-analysis.png" alt="Analyse du marché de l'énergie" className="w-full h-[400px]" />
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="em-container pb-24">
                <div className="relative bg-primary rounded-3xl text-primary-foreground px-8 py-14 md:px-16 md:py-20 grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-10 overflow-hidden">
                    <CmsBackground cmsKey="home.cta_strip.bg" />
                    <div className="relative z-10">
                        <h2
                            className="font-display text-3xl md:text-5xl font-bold tracking-tight"
                            dangerouslySetInnerHTML={{
                                __html: c("home.cta_strip.title", "Prêt à reprendre le contrôle<br /> de vos factures ?"),
                            }}
                        />
                        <p className="text-primary-foreground/80 mt-4 max-w-xl">
                            {c("home.cta_strip.body", "Démarrez en 2 minutes. Sans engagement, sans frais cachés. Energy Market vous accompagne du diagnostic à la signature.")}
                        </p>
                    </div>
                    <Button asChild size="lg" className="relative z-10 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-14 px-9 text-base" data-testid="bottom-cta">
                        <Link to="/devis">
                            {c("home.cta_strip.cta", "Recevoir mon devis gratuit")}
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Link>
                    </Button>
                </div>
            </section>
        </div>
    );
}
